import React, { FC, useContext } from 'react';
import { IAdminOverviewResponse } from '../../models/AdminOverviewResponse';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { LicenseExpiry } from '../LicenseExpiry/LicenseExpiry';
import { useTranslation } from 'react-i18next';
import { GuardedRouteLink } from '../../resources/GuardedRouteLink';
import {
  NoOrganizationContextProvider,
  OrganizationContextProviderLazy,
} from '../../contexts/OrganizationContextProvider';
import { LazyUser } from '../LazyData/LazyUser';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { Single } from '../../types/single';

interface IProps {
  licensesExpiring: IAdminOverviewResponse['licensesExpiring'];
}

const OrgLink: FC = () => {
  const { organization } = useContext(OrganizationContext);
  if (!organization) return null;
  return (
    <Link component={GuardedRouteLink} route={'OrganizationDetails'} params={[organization.name]} showFallback={true}>
      {organization.name}
    </Link>
  );
};

const SingleExpiration: FC<{ license: Single<IAdminOverviewResponse['licensesExpiring']> }> = ({ license }) => {
  const { organization } = useContext(OrganizationContext);

  return (
    <TableRow>
      <TableCell>
        <LazyUser id={license.organizationOwnerUuid} />
      </TableCell>
      <TableCell>
        <OrgLink />
      </TableCell>
      <TableCell>
        <NoOrganizationContextProvider>
          <LazyUser id={license.representativeUuid} />
        </NoOrganizationContextProvider>
      </TableCell>
      <TableCell>
        {license.plans.map((plan) => (
          <div key={`${plan.plan.id}/${plan.customerProduct.id}`}>
            <Link
              component={GuardedRouteLink}
              route={'OrganizationProduct'}
              params={[organization?.name, plan.customerProduct.id]}
              underline="hover"
              showFallback={true}
            >
              {plan.plan.name}
            </Link>
          </div>
        ))}
      </TableCell>
      <TableCell>
        {license.plans.map((plan) => (
          <div key={`${plan.plan.id}/${plan.customerProduct.id}`}>
            <LicenseExpiry expiry={plan.expiresAt} type={plan.plan.type} />
          </div>
        ))}
      </TableCell>
    </TableRow>
  );
};

export const NextExpiration: FC<IProps> = ({ licensesExpiring }) => {
  const { t } = useTranslation();

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t('adminOverview.expirations.customer')}</TableCell>
          <TableCell>{t('adminOverview.expirations.company')}</TableCell>
          <TableCell>{t('adminOverview.expirations.representative')}</TableCell>
          <TableCell>{t('adminOverview.expirations.plan')}</TableCell>
          <TableCell>{t('adminOverview.expirations.expires')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {licensesExpiring.map((license) => {
          return (
            <OrganizationContextProviderLazy
              id={license.organizationUuid}
              key={`${license.organizationUuid}-${license.plans.map((plan) => plan.customerProduct.id).join(',')}`}
            >
              <SingleExpiration license={license} />
            </OrganizationContextProviderLazy>
          );
        })}
      </TableBody>
    </Table>
  );
};
